/**
 * Answer module API-client functions.
 *
 * Mirrors `lib/quote-api.ts`'s shape — `apiFetch` + zod parse on the way
 * out. Surfaces the Answer routes shipped in M-ANSWER.2:
 *
 *   /v1/answer/documents               (PR #102)
 *   /v1/answer/questionnaires + detail (PR #103)
 *   /v1/answer/questionnaires/:id/generate (credit gate, PR #103)
 *   /v1/answer/questionnaires/:id/questions (PR #104)
 *   /v1/answer/answers/:id review      (PR #104)
 *   /v1/answer/library                 (PR #104)
 *   /v1/answer/questionnaires/:id/export.csv (PR #104)
 *
 * M-ANSWER.3 (this PR).
 */
import {
  type AnswerCitationResponse,
  type AnswerDocumentResponse,
  AnswerDocumentResponseSchema,
  type AnswerLibraryEntryResponse,
  AnswerLibraryEntryResponseSchema,
  type AnswerQuestionnaireDetailResponse,
  AnswerQuestionnaireDetailResponseSchema,
  type AnswerQuestionnaireResponse,
  AnswerQuestionnaireResponseSchema,
  AnswerQuestionResponseSchema,
  type AnswerResponseRecord,
  AnswerResponseRecordSchema,
  type CreateAnswerQuestionnaireRequest,
  type GenerateAnswerQuestionnaireResponse,
  GenerateAnswerQuestionnaireResponseSchema,
  type ImportAnswerQuestionsRequest,
  type PromoteAnswerLibraryRequest,
  type ReviewAnswerResponseRequest,
} from "@simpliwork/api-schema";
import { z } from "zod";
import { apiFetch, type GetTokenFn } from "./api-client";

const DocumentListSchema = z.object({ items: z.array(AnswerDocumentResponseSchema) });
const QuestionnaireListSchema = z.object({ items: z.array(AnswerQuestionnaireResponseSchema) });
const QuestionListSchema = z.object({ items: z.array(AnswerQuestionResponseSchema) });
const LibraryListSchema = z.object({ items: z.array(AnswerLibraryEntryResponseSchema) });

export type AnswerDocumentList = z.infer<typeof DocumentListSchema>;
export type AnswerQuestionnaireList = z.infer<typeof QuestionnaireListSchema>;
export type AnswerQuestionList = z.infer<typeof QuestionListSchema>;
export type AnswerLibraryList = z.infer<typeof LibraryListSchema>;
export type { AnswerCitationResponse }; // re-exported for the detail UI

type AuthOpt = { getToken?: GetTokenFn };

// ── documents ──────────────────────────────────────────────────────────────

export async function fetchAnswerDocuments(opts: AuthOpt = {}): Promise<AnswerDocumentList> {
  const json = await apiFetch<unknown>("/api/v1/answer/documents", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return DocumentListSchema.parse(json);
}

export async function fetchAnswerDocument(
  id: string,
  opts: AuthOpt = {},
): Promise<AnswerDocumentResponse> {
  const json = await apiFetch<unknown>(`/api/v1/answer/documents/${id}`, {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerDocumentResponseSchema.parse(json);
}

export async function uploadAnswerDocument(
  file: File,
  title: string | null,
  opts: AuthOpt = {},
): Promise<AnswerDocumentResponse> {
  const form = new FormData();
  form.append("file", file);
  if (title) form.append("title", title);
  const json = await apiFetch<unknown>("/api/v1/answer/documents", {
    method: "POST",
    body: form,
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerDocumentResponseSchema.parse(json);
}

export async function deleteAnswerDocument(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/answer/documents/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── questionnaires ─────────────────────────────────────────────────────────

export async function fetchAnswerQuestionnaires(
  opts: AuthOpt = {},
): Promise<AnswerQuestionnaireList> {
  const json = await apiFetch<unknown>("/api/v1/answer/questionnaires", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuestionnaireListSchema.parse(json);
}

export async function fetchAnswerQuestionnaire(
  id: string,
  opts: AuthOpt = {},
): Promise<AnswerQuestionnaireDetailResponse> {
  const json = await apiFetch<unknown>(`/api/v1/answer/questionnaires/${id}`, {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerQuestionnaireDetailResponseSchema.parse(json);
}

export async function createAnswerQuestionnaire(
  body: CreateAnswerQuestionnaireRequest,
  opts: AuthOpt = {},
): Promise<AnswerQuestionnaireResponse> {
  const json = await apiFetch<unknown>("/api/v1/answer/questionnaires", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerQuestionnaireResponseSchema.parse(json);
}

export async function deleteAnswerQuestionnaire(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/answer/questionnaires/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

export async function generateAnswerQuestionnaire(
  id: string,
  opts: AuthOpt = {},
): Promise<GenerateAnswerQuestionnaireResponse> {
  const json = await apiFetch<unknown>(`/api/v1/answer/questionnaires/${id}/generate`, {
    method: "POST",
    body: JSON.stringify({}),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return GenerateAnswerQuestionnaireResponseSchema.parse(json);
}

// ── questions (bulk import) ────────────────────────────────────────────────

export async function fetchAnswerQuestions(
  questionnaireId: string,
  opts: AuthOpt = {},
): Promise<AnswerQuestionList> {
  const json = await apiFetch<unknown>(
    `/api/v1/answer/questionnaires/${questionnaireId}/questions`,
    {
      ...(opts.getToken ? { getToken: opts.getToken } : {}),
    },
  );
  return QuestionListSchema.parse(json);
}

export async function importAnswerQuestions(
  questionnaireId: string,
  body: ImportAnswerQuestionsRequest,
  opts: AuthOpt = {},
): Promise<AnswerQuestionList> {
  const json = await apiFetch<unknown>(
    `/api/v1/answer/questionnaires/${questionnaireId}/questions`,
    {
      method: "POST",
      body: JSON.stringify(body),
      ...(opts.getToken ? { getToken: opts.getToken } : {}),
    },
  );
  return QuestionListSchema.parse(json);
}

// ── answer responses (review) ──────────────────────────────────────────────

export async function fetchAnswerResponse(
  id: string,
  opts: AuthOpt = {},
): Promise<AnswerResponseRecord> {
  const json = await apiFetch<unknown>(`/api/v1/answer/answers/${id}`, {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerResponseRecordSchema.parse(json);
}

export async function reviewAnswerResponse(
  id: string,
  body: ReviewAnswerResponseRequest,
  opts: AuthOpt = {},
): Promise<AnswerResponseRecord> {
  const json = await apiFetch<unknown>(`/api/v1/answer/answers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerResponseRecordSchema.parse(json);
}

// ── library ────────────────────────────────────────────────────────────────

export async function fetchAnswerLibrary(opts: AuthOpt = {}): Promise<AnswerLibraryList> {
  const json = await apiFetch<unknown>("/api/v1/answer/library", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return LibraryListSchema.parse(json);
}

export async function promoteAnswerLibrary(
  body: PromoteAnswerLibraryRequest,
  opts: AuthOpt = {},
): Promise<AnswerLibraryEntryResponse> {
  const json = await apiFetch<unknown>("/api/v1/answer/library", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return AnswerLibraryEntryResponseSchema.parse(json);
}

export async function deleteAnswerLibrary(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/answer/library/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── export ─────────────────────────────────────────────────────────────────

/** URL only — UI links directly to this endpoint; the API streams CSV. */
export function answerExportUrl(questionnaireId: string): string {
  return `/api/v1/answer/questionnaires/${questionnaireId}/export.csv`;
}
